import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  PostAdminV1StoresStoreIdInventoryAdjustBody,
  PutAdminV1StoresStoreIdInventoryReorderPointBody,
} from '@nucleus-commerce/api-client/admin';

import { productKeys } from '@/features/products';

import { inventoryApi } from '../api/inventory-api';

import { inventoryKeys } from './inventory-keys';

/**
 * A change moves the level, may move it in or out of the low-stock filter, adds a ledger row and
 * changes the per-store totals on product pages.
 */
function useInvalidateStock(storeId: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: inventoryKeys.levels(storeId) }),
      queryClient.invalidateQueries({ queryKey: inventoryKeys.movements(storeId) }),
      queryClient.invalidateQueries({
        queryKey: productKeys.details(),
        predicate: (q) => q.queryKey[4] === 'stock',
      }),
    ]);
}

/** 409 INSUFFICIENT_STOCK and 422 on the quantity are explained by the dialog. */
export function useAdjustStock(storeId: string) {
  const invalidate = useInvalidateStock(storeId);
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdInventoryAdjustBody) =>
      inventoryApi.adjust(storeId, body),
    meta: { successMessage: 'inventory:toasts.adjusted', silentError: true },
    onSuccess: invalidate,
  });
}

export function useSetReorderPoint(storeId: string) {
  const invalidate = useInvalidateStock(storeId);
  return useMutation({
    mutationFn: (body: PutAdminV1StoresStoreIdInventoryReorderPointBody) =>
      inventoryApi.setReorderPoint(storeId, body),
    meta: { successMessage: 'inventory:toasts.reorderPointSaved', silentError: true },
    onSuccess: invalidate,
  });
}

/** Switches tracking on the master variant; the variant then shows on every store's inventory. */
export function useSetTracking(storeId: string, productId: string, variantId: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateStock(storeId);
  return useMutation({
    mutationFn: (trackInventory: boolean) =>
      inventoryApi.setTracking(productId, variantId, trackInventory),
    meta: { successMessage: 'inventory:toasts.trackingOn' },
    onSuccess: async () => {
      await invalidate();
      await queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) });
    },
  });
}
